import { z } from "zod";
import { smbPrimaryCtaSchema, smbPublicContactSchema, smbProofSchema } from "./smb-intake";
import { smbLpItemSchema } from "./smb-lp-templates";
import { smbWebsiteFactsPatch } from "./smb-website-facts";

// Omitted keys retain their value. null explicitly clears that whole optional
// field. proofNotes is a structured adapter to intake.proof, never prose.
export const smbHomeOverridesSchema = z.object({
  primaryCta: smbPrimaryCtaSchema.nullable().optional(),
  publicContact: smbPublicContactSchema.nullable().optional(),
  proofNotes: smbProofSchema.nullable().optional(),
  tone: z.string().trim().min(1).max(255).nullable().optional(),
  claimsToAvoid: z.string().trim().min(1).max(2000).nullable().optional(),
}).strict();
export type SmbHomeOverrides = z.infer<typeof smbHomeOverridesSchema>;
export const smbHomeGenerateSchema = z.object({
  catalogKey: z.string().trim().min(1).max(160),
  requestIdentity: z.string().uuid(),
  overrides: smbHomeOverridesSchema.optional(),
  saveToBrand: z.boolean().default(false),
  previousPageId: z.string().uuid().optional(),
  feedback: z.string().trim().min(1).max(4000).optional(),
  currentCopy: z.string().min(1).max(100000).optional(),
  surface: z.literal("lp-express-2").optional(),
}).strict().superRefine((value, ctx) => {
  if (value.previousPageId && (!value.feedback || !value.currentCopy))
    ctx.addIssue({ code: "custom", message: "Regeneration requires feedback and complete current copy." });
  if (!value.previousPageId && (value.feedback || value.currentCopy))
    ctx.addIssue({ code: "custom", message: "Feedback and current copy require a previous page." });
});
export const smbHomeServiceReviewSchema = z.object({
  id: z.string().min(1).max(255), name: z.string().min(1).max(255),
  description: z.string().max(4000).nullable(),
}).strict();
export const smbHomePreviewSchema = z.object({
  businessInfoPreview: z.string().max(8000),
  missing: z.array(z.enum(["primary_cta", "public_contact", "proof", "work_photos"])).max(4),
  warnings: z.array(z.string().max(500)).max(100),
  facts: smbHomeOverridesSchema,
  services: z.array(smbHomeServiceReviewSchema).max(200),
  serviceAreas: z.array(z.string().min(1).max(255)).max(30),
  templates: z.array(smbLpItemSchema).max(1000),
}).strict();
export type SmbHomePreview = z.infer<typeof smbHomePreviewSchema>;

export function smbHomeContextPatch(overrides: SmbHomeOverrides): Record<string, unknown> {
  const validated = smbHomeOverridesSchema.parse(overrides);
  return smbWebsiteFactsPatch(Object.fromEntries(Object.entries(validated).map(([key, value]) =>
    [key === "proofNotes" ? "proof" : key, value])));
}

export function mergeSmbHomeContext(context: Record<string, unknown>, overrides: SmbHomeOverrides) {
  const result = { ...context };
  for (const [key, value] of Object.entries(smbHomeContextPatch(overrides))) {
    if (value === null) delete result[key];
    else result[key] = value;
  }
  return result;
}
